/**
 * Customer detail overview — data-bound S10 body.
 *
 * The route owns loading/error/archive orchestration; this component owns the
 * prototype-shaped overview content: outstanding card, recent invoices,
 * activity timeline, payment health, credit limit, notes, and quick actions.
 *
 * @see prototype/sales/customer.html — V1.1 baseline
 */
import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Archive,
  Bell,
  Check,
  ChevronDown,
  CreditCard,
  Download,
  Edit3,
  FileText,
  MessageCircle,
  Percent,
  Phone,
  Plus,
  TrendingUp,
  UserPlus,
  Zap,
} from "lucide-react";
import type { customers as customerContract } from "@flint/api-contract";
import type { z } from "zod";
import type { DetailStatStripMetrics } from "./detail-stat-strip.tsx";

type DetailCustomer = z.infer<typeof customerContract.customerRow>;

export interface InvoiceListItem {
  id: string;
  docNumber: string | null;
  issuedOn: string;
  dueOn: string;
  totalPaise: string;
  status: "draft" | "issued" | "partially_paid" | "paid" | "overdue" | "void" | "reversed";
}

export interface InvoiceListData {
  items: InvoiceListItem[];
  counts: {
    all: number;
    draft: number;
    issued: number;
    partially_paid: number;
    paid: number;
    overdue: number;
    void: number;
    reversed: number;
  };
  kpis: {
    outstandingPaise: string;
    overduePaise: string;
    overdueInvoiceCount: number;
    oldestOverdueDays: number | null;
  };
}

export interface CustomerDetailModel {
  summary: {
    invoiceCount: number;
    overdueInvoiceCount: number;
    oldestOverdueDays: number | null;
    behavior: string;
  };
  invoiceCount: number;
  activityCount: number;
  /** Convenience alias for `openInvoices[0] ?? null`. Kept for the
   *  AC16 reminder handler and any consumer that only needs the top-
   *  priority open invoice. */
  openInvoice: RecentInvoiceModel | null;
  /** All open invoices (overdue/issued/partially_paid) for this
   *  customer, sorted overdue-first then by earliest `dueOn`. Renders
   *  the Outstanding card list. */
  openInvoices: RecentInvoiceModel[];
  recentInvoices: RecentInvoiceModel[];
  activityEvents: ActivityEvent[];
  paymentHealth: PaymentHealthModel;
  credit: CreditModel;
  outstandingSub: string;
  recentSub: string;
  activitySub: string;
  metrics: DetailStatStripMetrics;
}

interface RecentInvoiceModel extends InvoiceListItem {
  docNumber: string;
  overdueDays: number;
  statusLabel: string;
}

interface ActivityEvent {
  tone: "paid" | "issued" | "reminder" | "late" | "note";
  icon: ReactNode;
  title: string;
  when: string;
  /** Optional attribution — e.g. "by Jasim · Founder". Renders in the head. */
  who?: string;
  body: ReactNode;
}

interface PaymentHealthModel {
  behavior: string;
  avgDelay: string;
  reliability: string;
  hasRisk: boolean;
  sparkline: Array<"on" | "late" | "miss" | "now" | "empty">;
  overdueCount: number;
  paidCount: number;
  openCount: number;
}

interface CreditModel {
  usedLabel: string;
  percent: number;
  percentLabel: string;
  headroomLabel: ReactNode;
  note: string;
}

export interface CustomerDetailOverviewEditActions {
  onEditTax: () => void;
  onEditTerms: () => void;
  onEditCommunication: () => void;
  onEditTags: () => void;
}

/**
 * Server-pagination state the route owns and threads through to the
 * activity timeline. `hasMore` reflects whether the `customers.activity`
 * infinite query has another audit page on the server; `onLoadMore`
 * triggers `fetchNextPage()`.
 */
export interface ActivityFetchState {
  hasMore: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
}

export function CustomerDetailOverview({
  customer,
  model,
  editActions,
  activityFetch,
  onViewAllInvoices,
  onOpenLedger,
  onSendReminder,
  reminderPending,
  reminderJustSent,
  reminderDisabledReason,
  onExportStatement,
  exportDisabledReason,
}: {
  customer: DetailCustomer;
  model: CustomerDetailModel;
  editActions?: CustomerDetailOverviewEditActions;
  activityFetch?: ActivityFetchState;
  onViewAllInvoices?: () => void;
  onOpenLedger?: () => void;
  onSendReminder?: (() => void) | undefined;
  reminderPending?: boolean | undefined;
  reminderJustSent?: boolean | undefined;
  reminderDisabledReason?: string | null | undefined;
  onExportStatement?: (() => void) | undefined;
  exportDisabledReason?: string | null | undefined;
}) {
  return (
    <div className="customer-detail-body-grid">
      <div className="customer-detail-body-main">
        <SectionCard
          title="Outstanding"
          sub={model.outstandingSub}
          action="View ledger"
          onAction={onOpenLedger}
        >
          {model.openInvoices.length > 0 ? (
            <ul className="customer-detail-inv-list">
              {model.openInvoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  tone={invoice.status === "overdue" ? "late" : "open"}
                  icon={
                    invoice.status === "overdue" ? (
                      <Bell size={12} strokeWidth={2.4} />
                    ) : (
                      <FileText size={12} strokeWidth={2.4} />
                    )
                  }
                  number={invoice.docNumber}
                  meta={`Issued ${formatDate(invoice.issuedOn)} · due ${formatDate(invoice.dueOn)}`}
                  amount={formatPaiseShort(invoice.totalPaise)}
                  status={invoice.statusLabel}
                  invoiceId={invoice.id}
                />
              ))}
            </ul>
          ) : (
            <EmptyInline
              title="No outstanding invoices"
              description="This customer has no issued or overdue balance on record."
            />
          )}
        </SectionCard>

        <SectionCard
          title="Recent invoices"
          sub={model.recentSub}
          {...(model.invoiceCount > 0
            ? { action: `View all ${model.invoiceCount}`, onAction: onViewAllInvoices }
            : {})}
        >
          {model.recentInvoices.length > 0 ? (
            <ul className="customer-detail-inv-list">
              {model.recentInvoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  tone={
                    invoice.status === "paid"
                      ? "paid"
                      : invoice.status === "overdue"
                        ? "late"
                        : "open"
                  }
                  icon={
                    invoice.status === "paid" ? (
                      <Check size={12} strokeWidth={2.4} />
                    ) : invoice.status === "overdue" ? (
                      <Bell size={12} strokeWidth={2.4} />
                    ) : (
                      <FileText size={12} strokeWidth={2.4} />
                    )
                  }
                  number={invoice.docNumber}
                  meta={`Issued ${formatDate(invoice.issuedOn)} · due ${formatDate(invoice.dueOn)}`}
                  amount={formatPaiseShort(invoice.totalPaise)}
                  status={invoice.statusLabel}
                />
              ))}
            </ul>
          ) : (
            <EmptyInline
              title="No invoices yet"
              description="Create this customer's first invoice to start the history."
            />
          )}
        </SectionCard>

        <SectionCard title="Activity timeline" sub={model.activitySub}>
          <ActivityTimeline
            events={model.activityEvents}
            customerName={shortName(customer.name)}
            {...(activityFetch ? { fetch: activityFetch } : {})}
          />
        </SectionCard>
      </div>

      <aside className="customer-detail-body-side">
        <PaymentHealthCard model={model.paymentHealth} />
        <TaxAndInvoicingCard
          customer={customer}
          {...(editActions ? { onEdit: editActions.onEditTax } : {})}
        />
        <TermsAndCreditCard
          customer={customer}
          model={model.credit}
          {...(editActions ? { onEdit: editActions.onEditTerms } : {})}
        />
        <QuickActions
          customerId={customer.id}
          name={shortName(customer.name)}
          phone={customer.phone}
          onSendReminder={onSendReminder}
          reminderPending={reminderPending}
          reminderJustSent={reminderJustSent}
          reminderDisabledReason={reminderDisabledReason}
          onAddNote={editActions?.onEditTags}
          onExportStatement={onExportStatement}
          exportDisabledReason={exportDisabledReason}
        />
      </aside>
    </div>
  );
}

export function CustomerDetailEmptyTab({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="customer-detail-empty">
      <span className="ic">
        <FileText size={18} strokeWidth={2} aria-hidden />
      </span>
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

/**
 * Activity tab body — reuses the same `ActivityTimeline` the Overview
 * card renders so the tab shows the real event list instead of a stub.
 * The tab-count pill and this list are driven by the same
 * `model.activityEvents`, so the count and the visible rows always
 * agree.
 */
export function CustomerActivityTab({
  model,
  customerName,
  activityFetch,
}: {
  model: CustomerDetailModel;
  customerName: string;
  activityFetch?: ActivityFetchState;
}) {
  return (
    <SectionCard title="Activity timeline" sub={model.activitySub}>
      <ActivityTimeline
        events={model.activityEvents}
        customerName={shortName(customerName)}
        {...(activityFetch ? { fetch: activityFetch } : {})}
      />
    </SectionCard>
  );
}

/**
 * Invoices tab body — renders every invoice for this customer using
 * the same row visual the Overview uses. Not sliced to the Recent-8
 * limit that `buildCustomerDetailModel` applies; the tab is the "show
 * me everything" surface, so it maps the raw list directly. An empty
 * list keeps the honest empty state; a wired `View all N` on the
 * Overview also lands here (via tab switch or `/sales/invoices?customerId`).
 */
export function CustomerInvoicesList({ items }: { items: InvoiceListItem[] }) {
  if (items.length === 0) {
    return (
      <CustomerDetailEmptyTab
        title="No invoices yet"
        description="This customer has no invoices on record. Create one to start their history."
      />
    );
  }
  const rows = items.map(toRecentInvoiceModel);
  return (
    <ul className="customer-detail-inv-list">
      {rows.map((invoice) => (
        <InvoiceRow
          key={invoice.id}
          tone={
            invoice.status === "paid"
              ? "paid"
              : invoice.status === "overdue"
                ? "late"
                : "open"
          }
          icon={
            invoice.status === "paid" ? (
              <Check size={12} strokeWidth={2.4} />
            ) : invoice.status === "overdue" ? (
              <Bell size={12} strokeWidth={2.4} />
            ) : (
              <FileText size={12} strokeWidth={2.4} />
            )
          }
          number={invoice.docNumber}
          meta={`Issued ${formatDate(invoice.issuedOn)} · due ${formatDate(invoice.dueOn)}`}
          amount={formatPaiseShort(invoice.totalPaise)}
          status={invoice.statusLabel}
        />
      ))}
    </ul>
  );
}

export type CustomerActivityEntry = z.infer<typeof customerContract.activityRow>;

export function buildCustomerDetailModel(
  customer: DetailCustomer,
  list: InvoiceListData | undefined,
  activity: readonly CustomerActivityEntry[] | undefined = undefined,
): CustomerDetailModel {
  const items = list?.items ?? [];
  const counts = list?.counts ?? {
    all: items.length,
    draft: items.filter((i) => i.status === "draft").length,
    issued: items.filter((i) => i.status === "issued").length,
    partially_paid: items.filter((i) => i.status === "partially_paid").length,
    paid: items.filter((i) => i.status === "paid").length,
    overdue: items.filter((i) => i.status === "overdue").length,
    void: items.filter((i) => i.status === "void").length,
  };
  const openCount = counts.issued + counts.partially_paid + counts.overdue;
  const invoiceCount = counts.all;
  const outstandingPaise = BigInt(list?.kpis.outstandingPaise ?? "0");
  const overduePaise = BigInt(list?.kpis.overduePaise ?? "0");
  const hasOpenBalance = outstandingPaise > 0n;
  const openBalanceCount = hasOpenBalance ? openCount : 0;
  const riskOverdueCount =
    overduePaise > 0n ? (list?.kpis.overdueInvoiceCount ?? counts.overdue) : 0;
  const oldestOverdueDays =
    riskOverdueCount > 0 ? (list?.kpis.oldestOverdueDays ?? deriveOldestOverdueDays(items)) : null;
  const ytdPaise = sumYtd(items);
  const creditLimitPaise = customer.creditLimitPaise ? BigInt(customer.creditLimitPaise) : null;
  const credit = buildCreditModel(outstandingPaise, creditLimitPaise);
  const behavior =
    riskOverdueCount > 0
      ? `${riskOverdueCount} overdue`
      : invoiceCount === 0
        ? "No invoice history yet"
        : hasOpenBalance
          ? "No overdue invoices"
          : "No open balance";

  const recentInvoices = items.slice(0, 8).map(toRecentInvoiceModel);
  const openInvoices = hasOpenBalance
    ? [...items]
        .filter(
          (i) => i.status === "overdue" || i.status === "issued" || i.status === "partially_paid",
        )
        .sort((a, b) => {
          if (a.status === "overdue" && b.status !== "overdue") return -1;
          if (b.status === "overdue" && a.status !== "overdue") return 1;
          return new Date(a.dueOn).getTime() - new Date(b.dueOn).getTime();
        })
        .map(toRecentInvoiceModel)
    : [];
  const openInvoice = openInvoices[0] ?? null;

  const activityEvents = buildActivityEvents(items, customer, riskOverdueCount > 0, activity);
  const paidOrOpen = counts.paid + openBalanceCount;
  const paymentHealth: PaymentHealthModel = {
    behavior,
    avgDelay: "Needs receipts",
    reliability:
      paidOrOpen > 0
        ? `${counts.paid} of ${paidOrOpen} paid`
        : invoiceCount > 0
          ? "No open balance"
          : "No invoice history",
    hasRisk: riskOverdueCount > 0,
    sparkline: buildSparkline(items),
    overdueCount: riskOverdueCount,
    paidCount: counts.paid,
    openCount: openBalanceCount,
  };

  return {
    summary: {
      invoiceCount,
      overdueInvoiceCount: riskOverdueCount,
      oldestOverdueDays,
      behavior,
    },
    invoiceCount,
    activityCount: activityEvents.length,
    openInvoice,
    openInvoices,
    recentInvoices,
    activityEvents,
    paymentHealth,
    credit,
    outstandingSub:
      outstandingPaise > 0n
        ? `${openBalanceCount} ${plural(openBalanceCount, "invoice")} · ${formatPaiseShort(outstandingPaise)} outstanding${oldestOverdueDays ? ` · oldest ${oldestOverdueDays} days past due` : ""}`
        : "No invoices outstanding",
    recentSub:
      invoiceCount > 0
        ? `Last ${recentInvoices.length} shown · ${invoiceCount} ${plural(invoiceCount, "invoice")} · ${counts.paid} paid · ${openBalanceCount} open balance`
        : "No invoices yet",
    activitySub:
      activityEvents.length > 0
        ? `${activityEvents.length} customer and invoice events from available data`
        : "No customer activity yet",
    metrics: {
      outstandingValue: formatPaiseValue(outstandingPaise).value,
      outstandingUnit: formatPaiseValue(outstandingPaise).unit,
      outstandingMeta:
        outstandingPaise > 0n ? (
          <>
            <span className={riskOverdueCount > 0 ? "delta dn" : "delta"}>
              {openBalanceCount} {plural(openBalanceCount, "invoice")}
            </span>
            <span>·</span>
            <span>
              {riskOverdueCount > 0 ? `${oldestOverdueDays ?? 0} days overdue` : "open balance"}
            </span>
          </>
        ) : invoiceCount === 0 ? (
          <span>No invoices yet</span>
        ) : (
          <span>No open balance</span>
        ),
      outstandingTone: riskOverdueCount > 0 ? "rose" : "indigo",
      ytdValue: formatPaiseValue(ytdPaise).value,
      ytdUnit: formatPaiseValue(ytdPaise).unit,
      ytdMeta:
        invoiceCount === 0 ? (
          <span>Lights up after first invoice</span>
        ) : (
          <>
            <span className="delta">
              {invoiceCount} {plural(invoiceCount, "invoice")}
            </span>
            <span>·</span>
            <span>{counts.paid} paid</span>
          </>
        ),
      avgPaymentValue: "—",
      avgPaymentUnit: "",
      avgPaymentMeta:
        invoiceCount === 0 ? (
          <span>Tracks once 2+ invoices are paid</span>
        ) : (
          <span>Needs receipt history</span>
        ),
      creditValue: credit.percent > 0 ? String(credit.percent) : "—",
      creditUnit: credit.percent > 0 ? "%" : "",
      creditMeta: creditLimitPaise ? (
        <>
          <span className="num">{formatPaiseShort(outstandingPaise)}</span>
          <span>/</span>
          <span className="num">{formatPaiseShort(creditLimitPaise)}</span>
          <span>·</span>
          <span>limit</span>
        </>
      ) : invoiceCount === 0 ? (
        <span>Full limit available · company default</span>
      ) : (
        <span>No limit set</span>
      ),
      creditTone: credit.percent >= 75 ? "amber" : "indigo",
    },
  };
}

function SectionCard({
  title,
  sub,
  action,
  onAction,
  actionDisabledReason,
  children,
}: {
  title: string;
  sub: string;
  action?: string | undefined;
  onAction?: (() => void) | undefined;
  actionDisabledReason?: string | undefined;
  children: ReactNode;
}) {
  const disabled = Boolean(actionDisabledReason);
  return (
    <section className="customer-detail-section">
      <header className="customer-detail-section-head">
        <div>
          <h3>{title}</h3>
          <p className="sub">{sub}</p>
        </div>
        {action ? (
          <button
            className="customer-detail-ghost"
            type="button"
            onClick={disabled ? undefined : onAction}
            disabled={disabled}
            title={actionDisabledReason}
            aria-disabled={disabled || undefined}
          >
            {action}
          </button>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function InvoiceRow({
  tone,
  icon,
  number,
  meta,
  amount,
  status,
  invoiceId,
}: {
  tone: "paid" | "late" | "open";
  icon: ReactNode;
  number: string;
  meta: string;
  amount: string;
  status: string;
  /**
   * When provided, wraps the row contents in a `<Link>` so the whole
   * row navigates to the invoice detail. Used by the Outstanding card
   * to make every open-invoice row clickable; Recent invoices keeps
   * the plain row shape for now.
   */
  invoiceId?: string;
}) {
  const body = (
    <>
      <span className={`ic ${tone}`} aria-hidden>
        {icon}
      </span>
      <div className="copy">
        <div className="num">{number}</div>
        <div className="meta">{meta}</div>
      </div>
      <div className={`amt ${tone === "paid" ? "paid" : ""}`}>{amount}</div>
      <span className={`status ${tone}`}>{status}</span>
    </>
  );
  return (
    <li>
      {invoiceId ? (
        <Link
          to="/sales/invoices/$invoiceId"
          params={{ invoiceId }}
          className="customer-detail-inv-link"
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </li>
  );
}

function ActivityTimeline({
  events,
  customerName,
  fetch: fetchState,
}: {
  events: ActivityEvent[];
  customerName: string;
  /**
   * Server-pagination handles. When the cursor query has another page,
   * we show a "Load more" button at the bottom. Disabled while fetching
   * so a double-click doesn't spam the API.
   */
  fetch?: ActivityFetchState;
}) {
  if (events.length === 0) {
    return (
      <EmptyInline
        title="No activity yet"
        description={`${customerName} has no invoice or communication events yet.`}
      />
    );
  }

  const showLoadMore = fetchState?.hasMore === true;

  return (
    <>
      <ol className="customer-detail-timeline">
        {events.map((event, index) => (
          <TimelineItem
            key={`${event.title}-${event.when}-${index}`}
            tone={event.tone}
            icon={event.icon}
            title={event.title}
            when={event.when}
            {...(event.who ? { who: event.who } : {})}
            isNow={index === 0 && event.tone === "reminder"}
            isLast={index === events.length - 1 && !showLoadMore}
          >
            {event.body}
          </TimelineItem>
        ))}
      </ol>
      {showLoadMore ? (
        <div className="customer-detail-timeline-loadmore">
          <button type="button" onClick={fetchState?.onLoadMore} disabled={fetchState?.isFetching}>
            {fetchState?.isFetching ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </>
  );
}

function TimelineItem({
  tone,
  icon,
  title,
  when,
  who,
  isNow = false,
  isLast = false,
  children,
}: {
  tone: "paid" | "issued" | "reminder" | "late" | "note";
  icon: ReactNode;
  title: string;
  when: string;
  who?: string;
  isNow?: boolean;
  isLast?: boolean;
  children: ReactNode;
}) {
  return (
    <li className={`timeline-item s-${tone}${isNow ? " is-now" : ""}${isLast ? " is-last" : ""}`}>
      <span className="timeline-marker" aria-hidden>
        {icon}
      </span>
      <div className="timeline-content">
        <div className="head">
          <span className="what">{title}</span>
          <span className="when">{when}</span>
          {who ? <span className="who">{who}</span> : null}
        </div>
        <div className="body">{children}</div>
      </div>
    </li>
  );
}

function PaymentHealthCard({ model }: { model: PaymentHealthModel }) {
  return (
    <SideCard title="Payment health" icon={<TrendingUp size={13} strokeWidth={2.2} />}>
      <HealthRow label="Behavior" value={model.behavior} tone={model.hasRisk ? "warn" : "good"} />
      <HealthRow label="Avg delay" value={model.avgDelay} mono />
      <HealthRow
        label="Reliability"
        value={model.reliability}
        tone={model.hasRisk ? "warn" : "good"}
        mono
      />
      <HealthRow label="Disputed ever" value="No data" />
      <div className="customer-detail-pay-sparkline-label">
        <span>Recent invoices</span>
        <span className="num">oldest → today</span>
      </div>
      <div className="customer-detail-pay-sparkline">
        {model.sparkline.map((state, index) => (
          <span key={`${state}-${index}`} className={`pay-dot ${state}`} />
        ))}
      </div>
      <div className="customer-detail-credit-meta">
        <span>
          <span className="num">{model.overdueCount} overdue</span> ·{" "}
          <span className="num">{model.paidCount} paid</span>
        </span>
        <span>
          <span className="num">{model.openCount} open</span>
        </span>
      </div>
    </SideCard>
  );
}

/**
 * Pretty labels for the persisted `tax_mode` enum. Kept here next to
 * its consumer rather than lifting to a shared util — the card is the
 * only render site today and the enum is small.
 */
const TAX_MODE_LABEL: Record<DetailCustomer["taxMode"], string> = {
  cgst_sgst: "CGST + SGST",
  igst: "IGST",
  zero_rated: "Zero-rated",
  exempt: "Exempt",
};

/**
 * Read-only mirror of the Tax & invoicing dialog. Surfaces all six
 * tax-related columns so an operator can audit the buyer's defaults
 * without re-opening the editor. Clicking "Edit" lifts the same
 * EditTaxDialog the cover-dropdown and CompletionStrip already open.
 */
function TaxAndInvoicingCard({
  customer,
  onEdit,
}: {
  customer: DetailCustomer;
  onEdit?: () => void;
}) {
  const gstRate =
    customer.defaultGstRatePct === null ? "Not set" : `${customer.defaultGstRatePct}%`;
  const tdsLabel = customer.isTdsApplicable
    ? customer.tdsSection
      ? `Yes · Section ${customer.tdsSection}`
      : "Yes · section pending"
    : "No";
  const summary = `${TAX_MODE_LABEL[customer.taxMode]} · TDS ${customer.isTdsApplicable ? (customer.tdsSection ?? "on") : "off"}`;
  return (
    <SideCard
      title="Tax & invoicing"
      icon={<Percent size={13} strokeWidth={2.2} />}
      action="Edit"
      collapsible
      defaultCollapsed
      collapsedSummary={summary}
      {...(onEdit ? { onAction: onEdit } : {})}
    >
      <HealthRow label="Tax mode" value={TAX_MODE_LABEL[customer.taxMode]} />
      <HealthRow label="Default HSN / SAC" value={customer.defaultHsn ?? "—"} mono />
      <HealthRow label="Default GST rate" value={gstRate} mono />
      <HealthRow
        label="E-invoice (IRN)"
        value={customer.eInvoiceRequired ? "Required" : "Not required"}
        tone={customer.eInvoiceRequired ? "good" : undefined}
      />
      <HealthRow
        label="E-way bill on dispatch"
        value={customer.eWayBillRequired ? "Required" : "Not required"}
        tone={customer.eWayBillRequired ? "good" : undefined}
      />
      <HealthRow
        label="TDS on collection"
        value={tdsLabel}
        tone={customer.isTdsApplicable ? "good" : undefined}
      />
    </SideCard>
  );
}

function TermsAndCreditCard({
  customer,
  model,
  onEdit,
}: {
  customer: DetailCustomer;
  model: CreditModel;
  onEdit?: () => void;
}) {
  const terms = customer.paymentTerms ?? "Net 15";
  const termsIsDefault = !customer.paymentTerms;
  const openingPaise = customer.openingBalancePaise ? BigInt(customer.openingBalancePaise) : null;
  const summary = `${terms} · ${model.percentLabel}`;
  return (
    <SideCard
      title="Terms & credit"
      icon={<CreditCard size={13} strokeWidth={2.2} />}
      action="Edit"
      collapsible
      defaultCollapsed
      collapsedSummary={summary}
      {...(onEdit ? { onAction: onEdit } : {})}
    >
      <HealthRow label="Payment terms" value={termsIsDefault ? `${terms} (default)` : terms} />
      {customer.customerCategory ? (
        <HealthRow label="Category" value={customer.customerCategory} />
      ) : null}
      {customer.defaultRevenueGl ? (
        <HealthRow label="Default revenue GL" value={customer.defaultRevenueGl} mono />
      ) : null}
      {customer.earlyDiscount ? (
        <HealthRow label="Early-pay discount" value={customer.earlyDiscount} />
      ) : null}
      <HealthRow label="Credit used" value={model.usedLabel} mono />
      <div className="customer-detail-credit-bar">
        <i style={{ width: `${model.percent}%` }} />
      </div>
      <div className="customer-detail-credit-meta">
        <span className={model.percent >= 75 ? "warn" : ""}>{model.percentLabel}</span>
        <span>{model.headroomLabel}</span>
      </div>
      {openingPaise && openingPaise > 0n ? (
        <HealthRow
          label="Opening balance"
          value={`${formatPaiseShort(openingPaise)}${customer.openingBalanceAsOf ? ` · as of ${customer.openingBalanceAsOf}` : ""}`}
          mono
        />
      ) : null}
      <p className="customer-detail-side-note">{model.note}</p>
    </SideCard>
  );
}

function QuickActions({
  customerId,
  name,
  phone,
  onSendReminder,
  reminderPending,
  reminderJustSent,
  reminderDisabledReason,
  onAddNote,
  onExportStatement,
  exportDisabledReason,
}: {
  customerId: string;
  name: string;
  phone: string | null;
  onSendReminder?: (() => void) | undefined;
  reminderPending?: boolean | undefined;
  reminderJustSent?: boolean | undefined;
  reminderDisabledReason?: string | null | undefined;
  onAddNote?: (() => void) | undefined;
  onExportStatement?: (() => void) | undefined;
  exportDisabledReason?: string | null | undefined;
}) {
  return (
    <SideCard title="Quick actions" icon={<Zap size={13} strokeWidth={2.2} />}>
      <Link
        className="customer-detail-quick-action primary"
        to="/sales/invoices/new"
        search={{ customerId }}
      >
        <Plus size={14} strokeWidth={2.4} aria-hidden />
        New invoice for {name}
      </Link>
      <button
        className="customer-detail-quick-action"
        type="button"
        onClick={onSendReminder}
        disabled={Boolean(reminderDisabledReason) || reminderPending}
        title={reminderDisabledReason ?? undefined}
        aria-disabled={Boolean(reminderDisabledReason) || undefined}
      >
        <MessageCircle size={14} strokeWidth={2} aria-hidden />
        {reminderPending
          ? "Sending…"
          : reminderJustSent
            ? "Reminder sent"
            : "Send WhatsApp reminder"}
      </button>
      <a className="customer-detail-quick-action" href={phone ? `tel:+91${phone}` : undefined}>
        <Phone size={14} strokeWidth={2} aria-hidden />
        Call {name}
      </a>
      <button
        className="customer-detail-quick-action"
        type="button"
        onClick={onAddNote}
        disabled={!onAddNote}
        aria-disabled={!onAddNote || undefined}
      >
        <Edit3 size={14} strokeWidth={2} aria-hidden />
        Add internal note
      </button>
      <Link
        className="customer-detail-quick-action"
        to="/sales/customers/$customerId/ledger"
        params={{ customerId }}
      >
        <TrendingUp size={14} strokeWidth={2} aria-hidden />
        View ledger
      </Link>
      <button
        className="customer-detail-quick-action"
        type="button"
        onClick={onExportStatement}
        disabled={Boolean(exportDisabledReason)}
        title={exportDisabledReason ?? undefined}
        aria-disabled={Boolean(exportDisabledReason) || undefined}
      >
        <Download size={14} strokeWidth={2} aria-hidden />
        Export statement
      </button>
    </SideCard>
  );
}

function SideCard({
  title,
  icon,
  action,
  onAction,
  actionDisabled,
  collapsible = false,
  defaultCollapsed = false,
  collapsedSummary,
  children,
}: {
  title: string;
  /** Optional 13px lucide icon rendered just before the title. */
  icon?: ReactNode;
  action?: string | undefined;
  onAction?: (() => void) | undefined;
  actionDisabled?: boolean;
  /** When true, the body collapses behind a chevron in the header. */
  collapsible?: boolean;
  /** Initial collapsed state. Only meaningful when collapsible. */
  defaultCollapsed?: boolean;
  /**
   * Optional one-line glance shown next to the title when collapsed —
   * e.g. "CGST+SGST · TDS off". Keeps the card useful at a glance
   * without expanding it.
   */
  collapsedSummary?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(collapsible ? defaultCollapsed : false);

  const handleHeaderToggle = collapsible ? () => setCollapsed((prev) => !prev) : undefined;

  return (
    <section className="customer-detail-side-card">
      <header
        className="customer-detail-side-card-head"
        {...(handleHeaderToggle
          ? {
              role: "button",
              tabIndex: 0,
              onClick: handleHeaderToggle,
              onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleHeaderToggle();
                }
              },
              "aria-expanded": !collapsed,
              style: { cursor: "pointer" },
            }
          : {})}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
          {collapsible ? (
            <ChevronDown
              size={13}
              strokeWidth={2.2}
              aria-hidden
              style={{
                transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
                color: "var(--muted)",
                flexShrink: 0,
              }}
            />
          ) : null}
          {icon ? (
            <span
              aria-hidden
              style={{
                display: "inline-flex",
                color: "var(--muted)",
                flexShrink: 0,
              }}
            >
              {icon}
            </span>
          ) : null}
          <h4 style={{ margin: 0 }}>{title}</h4>
          {collapsed && collapsedSummary ? (
            <span
              style={{
                fontSize: "12px",
                color: "var(--muted)",
                marginLeft: "0.25rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {collapsedSummary}
            </span>
          ) : null}
        </div>
        {action ? (
          <button
            className="edit"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAction?.();
            }}
            disabled={actionDisabled}
            title={actionDisabled ? "Not editable yet" : undefined}
            style={actionDisabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
          >
            {action === "Edit" ? (
              <Edit3 size={11} strokeWidth={2} aria-hidden />
            ) : (
              <Plus size={11} strokeWidth={2.4} aria-hidden />
            )}
            {action}
          </button>
        ) : null}
      </header>
      {collapsed ? null : <div className="customer-detail-side-card-body">{children}</div>}
    </section>
  );
}

function HealthRow({
  label,
  value,
  tone,
  mono = false,
}: {
  label: string;
  value: string;
  tone?: "warn" | "good" | undefined;
  mono?: boolean | undefined;
}) {
  return (
    <div className="customer-detail-health-row">
      <span className="lbl">{label}</span>
      <span className={`val${tone ? ` ${tone}` : ""}${mono ? " num" : ""}`}>{value}</span>
    </div>
  );
}

function EmptyInline({ title, description }: { title: string; description: string }) {
  return (
    <div className="customer-detail-inline-empty">
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

function toRecentInvoiceModel(invoice: InvoiceListItem): RecentInvoiceModel {
  const overdueDays = invoice.status === "overdue" ? daysPast(invoice.dueOn) : 0;
  return {
    ...invoice,
    docNumber: invoice.docNumber ?? "Draft invoice",
    overdueDays,
    statusLabel:
      invoice.status === "overdue"
        ? `Overdue · ${overdueDays}d`
        : invoice.status === "issued"
          ? `Due ${formatDateShort(invoice.dueOn)}`
          : invoice.status === "partially_paid"
            ? `Part paid · due ${formatDateShort(invoice.dueOn)}`
            : invoice.status.replaceAll("_", " "),
  };
}

/**
 * Human labels for the customer columns the audit row can flag in
 * `metadata.changedKeys`. Anything not listed falls through to the raw
 * key (which is still human-readable in camelCase).
 */
const CHANGED_KEY_LABELS: Record<string, string> = {
  name: "Name",
  contactName: "Contact name",
  gstin: "GSTIN",
  phone: "Primary phone",
  phoneSecondary: "Secondary phone",
  whatsappOnSecondary: "WhatsApp slot",
  email: "Email",
  addressLine1: "Address",
  addressLine2: "Address (line 2)",
  state: "State",
  city: "City",
  pincode: "PIN code",
  paymentTerms: "Payment terms",
  creditLimitPaise: "Credit limit",
  openingBalancePaise: "Opening balance",
  openingBalanceAsOf: "Opening balance date",
  isTdsApplicable: "TDS on collection",
  tdsSection: "TDS section",
  tags: "Tags",
  internalNote: "Internal note",
  taxMode: "Tax mode",
  defaultHsn: "Default HSN",
  defaultGstRatePct: "Default GST rate",
  eInvoiceRequired: "E-invoice flag",
  eWayBillRequired: "E-way bill flag",
  customerCategory: "Category",
  defaultRevenueGl: "Revenue GL",
  earlyDiscount: "Early-pay discount",
  defaultChannel: "Channel",
  remind7dBefore: "Reminder 7d before",
  remind1dBefore: "Reminder 1d before",
  remindOnDue: "Reminder on due",
  remind7dAfter: "Reminder 7d after",
  monthlyStatement: "Monthly statement",
  marketingOptIn: "Marketing opt-in",
};

function friendlyKey(key: string): string {
  return CHANGED_KEY_LABELS[key] ?? key;
}

/**
 * Compose a one-line attribution for the audit event. For `web` actors
 * we have a real user name + membership role; for service actors we
 * label generically so the audit isn't anonymous.
 */
function describeActor(entry: CustomerActivityEntry): string | undefined {
  if (entry.actorType === "web") {
    if (!entry.actorName) return undefined;
    return entry.actorRole ? `by ${entry.actorName} · ${entry.actorRole}` : `by ${entry.actorName}`;
  }
  switch (entry.actorType) {
    case "system":
      return "by Flint";
    case "ai":
      return "by Flint AI";
    case "whatsapp":
      return "via WhatsApp";
    case "api":
      return "via integration";
    default:
      return undefined;
  }
}

function buildActivityEvents(
  items: InvoiceListItem[],
  customer: DetailCustomer,
  includeReminder: boolean,
  activity: readonly CustomerActivityEntry[] | undefined,
): ActivityEvent[] {
  const overdue = items.find((i) => i.status === "overdue");
  const events: Array<ActivityEvent & { _ts: number }> = [];

  // 1. Reminder (always rendered first, no timestamp — synthetic).
  if (overdue && includeReminder) {
    events.push({
      _ts: Number.POSITIVE_INFINITY,
      tone: "reminder",
      icon: <Bell size={14} strokeWidth={2.2} />,
      title: `Reminder ready · ${overdue.docNumber ?? "invoice"}`,
      when: `${daysPast(overdue.dueOn)}d overdue`,
      body: (
        <>
          Reminder can reference the{" "}
          <span className="num">{formatPaiseShort(overdue.totalPaise)}</span> balance due on{" "}
          <span className="num">{formatDate(overdue.dueOn)}</span>.
        </>
      ),
    });
  }

  // 2. Audit-sourced edit events (created / updated / archived).
  const auditEntries = activity ?? [];
  let hasCreatedAudit = false;
  for (const entry of auditEntries) {
    const ts = new Date(entry.at).getTime();
    const who = describeActor(entry);
    if (entry.verb === "created") {
      hasCreatedAudit = true;
      events.push({
        _ts: ts,
        tone: "issued",
        icon: <UserPlus size={14} strokeWidth={2.2} />,
        title: "Customer created",
        when: formatDate(entry.at),
        ...(who ? { who } : {}),
        body: <>Master record created for {shortName(customer.name)}.</>,
      });
    } else if (entry.verb === "archived") {
      events.push({
        _ts: ts,
        tone: "late",
        icon: <Archive size={14} strokeWidth={2.2} />,
        title: "Customer archived",
        when: formatDate(entry.at),
        ...(who ? { who } : {}),
        body: <>Soft-deleted; hidden from active list and dropdowns.</>,
      });
    } else if (entry.verb === "updated") {
      const changedKeys = Array.isArray(entry.metadata.changedKeys)
        ? (entry.metadata.changedKeys as string[])
        : [];
      const summary =
        changedKeys.length === 0
          ? "Master record updated"
          : changedKeys.length <= 3
            ? changedKeys.map(friendlyKey).join(", ")
            : `${changedKeys.slice(0, 2).map(friendlyKey).join(", ")} +${changedKeys.length - 2} more`;
      events.push({
        _ts: ts,
        tone: "note",
        icon: <Edit3 size={14} strokeWidth={2.2} />,
        title: "Customer updated",
        when: formatDate(entry.at),
        ...(who ? { who } : {}),
        body: <>Changed {summary}</>,
      });
    }
  }

  // 3. Invoice events.
  for (const invoice of items.slice(0, 5)) {
    events.push({
      _ts: new Date(invoice.issuedOn).getTime(),
      tone: invoice.status === "paid" ? "paid" : invoice.status === "overdue" ? "late" : "issued",
      icon:
        invoice.status === "paid" ? (
          <Check size={14} strokeWidth={2.4} />
        ) : invoice.status === "overdue" ? (
          <MessageCircle size={14} strokeWidth={2.2} />
        ) : (
          <FileText size={14} strokeWidth={2.2} />
        ),
      title: `${invoice.status === "paid" ? "Invoice paid" : invoice.status === "overdue" ? "Invoice overdue" : "Invoice recorded"} · ${invoice.docNumber ?? "Draft invoice"}`,
      when: formatDate(invoice.issuedOn),
      body: (
        <>
          <span className="num">{formatPaiseShort(invoice.totalPaise)}</span> · due{" "}
          <span className="num">{formatDate(invoice.dueOn)}</span>
        </>
      ),
    });
  }

  // 4. Synthetic "Customer created" — only if the audit log didn't supply one.
  if (!hasCreatedAudit) {
    events.push({
      _ts: new Date(customer.createdAt).getTime(),
      tone: "issued",
      icon: <UserPlus size={14} strokeWidth={2.2} />,
      title: "Customer created",
      when: formatDate(customer.createdAt),
      body: <>Master record created for {shortName(customer.name)}.</>,
    });
  }

  // Sort newest-first; the synthetic reminder pins at the top.
  events.sort((a, b) => b._ts - a._ts);
  return events.map(({ _ts: _unused, ...rest }) => rest);
}

function buildSparkline(items: InvoiceListItem[]): Array<"on" | "late" | "miss" | "now" | "empty"> {
  const states = items
    .slice(0, 12)
    .reverse()
    .map((invoice) => {
      if (invoice.status === "paid") return "on";
      if (invoice.status === "overdue") return "late";
      if (invoice.status === "void") return "miss";
      return "now";
    });
  return [
    ...Array.from({ length: Math.max(0, 12 - states.length) }, () => "empty" as const),
    ...states,
  ];
}

function buildCreditModel(outstandingPaise: bigint, creditLimitPaise: bigint | null): CreditModel {
  if (!creditLimitPaise || creditLimitPaise <= 0n) {
    return {
      usedLabel: `${formatPaiseShort(outstandingPaise)} used · no limit`,
      percent: 0,
      percentLabel: "No limit set",
      headroomLabel: "Add limit",
      note: "Credit usage will calculate after a customer credit limit is set.",
    };
  }
  const rawPercent = Number((outstandingPaise * 100n) / creditLimitPaise);
  const percent = Math.min(100, Math.max(0, rawPercent));
  const headroom = creditLimitPaise - outstandingPaise;
  return {
    usedLabel: `${formatPaiseShort(outstandingPaise)} of ${formatPaiseShort(creditLimitPaise)}`,
    percent,
    percentLabel: `${percent}% used`,
    headroomLabel:
      headroom > 0n ? (
        <>
          <span className="num">{formatPaiseShort(headroom)}</span> headroom
        </>
      ) : (
        "Limit exceeded"
      ),
    note:
      percent >= 75
        ? "Founder review recommended before issuing additional credit."
        : "Credit headroom is available for the next invoice.",
  };
}

function sumYtd(items: InvoiceListItem[]): bigint {
  const year = new Date().getFullYear();
  return items
    .filter(
      (item) =>
        item.status !== "void" &&
        item.status !== "draft" &&
        new Date(item.issuedOn).getFullYear() === year,
    )
    .reduce((sum, item) => sum + BigInt(item.totalPaise), 0n);
}

function deriveOldestOverdueDays(items: InvoiceListItem[]): number | null {
  const days = items
    .filter((item) => item.status === "overdue")
    .map((item) => daysPast(item.dueOn));
  return days.length > 0 ? Math.max(...days) : null;
}

function daysPast(dateIso: string): number {
  const ms = Date.now() - new Date(dateIso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function formatDate(dateIso: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(dateIso));
}

function formatDateShort(dateIso: string): string {
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(
    new Date(dateIso),
  );
}

function formatPaiseShort(value: string | bigint): string {
  const paise = typeof value === "bigint" ? value : BigInt(value);
  if (paise === 0n) return "₹0";
  const rupees = Number(paise) / 100;
  if (Math.abs(rupees) >= 100000) return `₹${(rupees / 100000).toFixed(2)}L`;
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rupees)}`;
}

function formatPaiseValue(paise: bigint): { value: string; unit: string } {
  if (paise === 0n) return { value: "₹0", unit: "" };
  const rupees = Number(paise) / 100;
  if (Math.abs(rupees) >= 100000) return { value: `₹${(rupees / 100000).toFixed(2)}`, unit: "L" };
  return {
    value: `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(rupees)}`,
    unit: "",
  };
}

function plural(count: number, word: string): string {
  return count === 1 ? word : `${word}s`;
}

function shortName(name: string): string {
  return (
    name
      .replace(/\b(private|pvt|limited|ltd|llp|inc)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim() || name
  );
}
